import React, { useEffect, useRef, useState } from "react";
import { announceSlateApply } from "@/components/SlateBleedGuard";
import { toast } from "sonner";
import { CloudArrowUp, CheckCircle, X, FileCsv, Lightning, Rocket } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import FieldModelPanel from "@/components/FieldModelPanel";
import LeverageBoard from "@/components/LeverageBoard";
import CompetitorPoolGrid from "@/components/CompetitorPoolGrid";
import { TID } from "@/constants/testIds";

export default function Uploads({ onChanged, summary }) {
  const dkInputRef = useRef();
  const [dkMeta, setDkMeta] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [slates, setSlates] = useState([]);
  const [autoLoading, setAutoLoading] = useState(false);
  const [selectedSlate, setSelectedSlate] = useState("");
  // Slate-type filter (Sprint 46+): compact one-click filter above the
  // chip row. "all" is the default; other values match the backend
  // `slate_type` field on each slate doc. Persisted BOTH to localStorage
  // (instant restore, works offline / logged-out) AND to the user's
  // `prefs.slate_filter` on the server (syncs across devices — laptop
  // pick shows up on the phone Sunday morning). Server value wins on
  // hydration; localStorage is the offline fallback.
  const [slateFilter, setSlateFilter] = useState(() => {
    try { return localStorage.getItem("wp_slate_filter") || "all"; }
    catch (_) { return "all"; }
  });
  // Track whether we've hydrated from the server yet so the initial
  // hydration PATCH doesn't trip the change-listener into a redundant
  // write-back loop.
  const hydratedRef = useRef(false);
  useEffect(() => {
    try { localStorage.setItem("wp_slate_filter", slateFilter); }
    catch (_) { /* private mode */ }
    // Skip the very first render — that value came from localStorage /
    // the initial state hook, not from a user click. Also skip until
    // we've completed the server hydration to avoid clobbering the
    // stored server value with the localStorage default.
    if (!hydratedRef.current) return;
    api.updatePrefs({ slate_filter: slateFilter }).catch((e) => {
      // Non-blocking: localStorage is the source of truth if the server
      // is unreachable. Silent debug log for debuggers on the console.
      console.debug("updatePrefs(slate_filter) failed:", e?.message);
    });
  }, [slateFilter]);

  // Hydrate server-side pref on mount. If the user has a different
  // filter saved on another device, that wins over localStorage.
  useEffect(() => {
    (async () => {
      try {
        const r = await api.getPrefs();
        const serverFilter = r?.prefs?.slate_filter;
        if (serverFilter && typeof serverFilter === "string" && serverFilter !== slateFilter) {
          setSlateFilter(serverFilter);
        }
      } catch (e) {
        console.debug("getPrefs load failed:", e?.message);
      } finally {
        // Mark hydration complete so subsequent setSlateFilter calls
        // trigger the server PATCH.
        hydratedRef.current = true;
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.dkSlatesList();
        setSlates(r.slates || []);
      } catch (e) { console.debug("dkSlatesList load failed:", e?.message); }
    })();
  }, []);

  const refreshSlates = async () => {
    setAutoLoading(true);
    try {
      const r = await api.dkSlatesRefresh();
      if (r.ok) {
        toast.success(`Refreshed ${r.draftgroups_successful}/${r.draftgroups_attempted} DK slates`);
        const list = await api.dkSlatesList();
        setSlates(list.slates || []);
      } else {
        toast.error(r.reason || "Refresh failed");
      }
    } catch (e) {
      toast.error("Refresh failed: " + (e?.message || "unknown"));
    } finally {
      setAutoLoading(false);
    }
  };

  // Sprint 60.52 — race-proof slate switching. Every apply call attaches
  // an AbortController; a subsequent click cancels the previous request
  // so its response can never overwrite the newer state. Fixes the
  // "click A then B then C fast, cards freeze on A's data" pattern the
  // user hit on production.
  const _applyAbortRef = React.useRef(null);
  // Sprint 60.52c — hover prefetch. When the user hovers a slate button
  // for >200ms we prefetch the DK pool for that dg so subsequent clicks
  // hit warm cache. Backend endpoint (`/dk/slates/{dg}/apply`) is
  // idempotent; a prefetch is just a cheap warm-up.
  const _prefetchTimerRef = React.useRef(null);
  const _prefetchedRef = React.useRef(new Set());

  const prefetchSlate = (dg) => {
    if (!dg || _prefetchedRef.current.has(dg)) return;
    _prefetchTimerRef.current = setTimeout(() => {
      _prefetchedRef.current.add(dg);
      // Fire-and-forget GET-shape prefetch. The backend's dk_slates
      // GET endpoint (/dk/slates) already carries the pool so this
      // just warms the CDN/proxy path.
      api.dkSlates?.().catch(() => {});
    }, 200);
  };
  const cancelPrefetch = () => {
    if (_prefetchTimerRef.current) {
      clearTimeout(_prefetchTimerRef.current);
      _prefetchTimerRef.current = null;
    }
  };

  const applySlateById = async (dg) => {
    if (!dg) return;
    // Cancel any in-flight apply so its response can't overwrite the
    // newer state. The AbortController wires through axios via the
    // `signal` option (supported since axios 0.22).
    if (_applyAbortRef.current) {
      try { _applyAbortRef.current.abort(); } catch (_) { /* noop */ }
    }
    const ac = new AbortController();
    _applyAbortRef.current = ac;
    setAutoLoading(true);
    setSelectedSlate(String(dg));
    // Broadcast a "slate-loading" event so the GameTicker + PlayerPool
    // can render skeleton state instead of stale rows during the ~2-6s
    // apply window.
    try {
      window.dispatchEvent(new CustomEvent("wp:slate-loading", {
        detail: { dg, loading: true },
      }));
    } catch (_) { /* CustomEvent unsupported */ }
    try {
      const r = await api.dkSlateApply(parseInt(dg, 10), { signal: ac.signal });
      // Guard against out-of-order response — if a newer apply already
      // superseded us, ignore our own success payload silently.
      if (ac.signal.aborted) return;
      announceSlateApply(r);
      const nameForToast = r.label || "slate";
      toast.success(`Loaded ${nameForToast} · ${r.player_count} players · ${r.n_games} games`);
      setDkMeta({
        n_players: r.player_count,
        n_teams: 2 * r.n_games,
        n_games: r.n_games,
        source: "dk_slate_api",
      });
      try {
        window.dispatchEvent(new CustomEvent("wp:pool-refreshed", {
          detail: { source: "slate-apply", dg: r.dg, player_count: r.player_count },
        }));
      } catch (_) { /* CustomEvent unsupported */ }
      onChanged?.();
    } catch (e) {
      // AbortError is expected when the user clicks another slate —
      // suppress the toast so we don't spam them with "Failed to apply".
      if (e?.name === "CanceledError" || e?.name === "AbortError" || e?.code === "ERR_CANCELED") return;
      toast.error(e?.response?.data?.detail || "Failed to apply slate");
    } finally {
      if (_applyAbortRef.current === ac) _applyAbortRef.current = null;
      setAutoLoading(false);
      try {
        window.dispatchEvent(new CustomEvent("wp:slate-loading", {
          detail: { dg, loading: false },
        }));
      } catch (_) { /* noop */ }
    }
  };

  const applySlate = () => applySlateById(selectedSlate);

  const handleDK = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await api.uploadDK(file);
      setDkMeta(res);
      toast.success(`DK pool loaded: ${res.rows} players, ${res.games.length} games`);
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "DK upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleCompetitor = async () => {
    // Sprint 60.75 — Market Consensus ingest UI was removed from the
    // Slate Selection page. This handler is orphaned but kept as a
    // guarded stub so any lingering ref does not throw. External CSV
    // ingest is now admin-only via /admin.
    toast.info("Market Consensus ingest moved to the Admin page.");
  };

  const removeSource = async (s) => {
    await api.deleteCompetitor(s);
    toast.info(`${s} removed`);
    onChanged?.();
  };

  // Load a synthetic 12-game Milly Maker slate for QA / offseason optimizer
  // testing. Flows through the same parser + rebuild pipeline as a real DK
  // upload, so every downstream rule engine (Combat Stack Matrix, Anti-Chalk
  // Governor, Value Auto-Tune, Chalk Watch) treats it as a real slate.
  // Different seed each click → distinct test slates.
  // (Removed Sprint 52 — product is live-testing on real DK slates only.)

  return (
    <div className="space-y-4">
      <div
        data-testid="slate-selection-next-step"
        className="text-[12px] mono uppercase tracking-widest text-zinc-400 border-l-2 border-[#39FF14] pl-3 py-1"
      >
        Next step · once your slate is locked in, head to{" "}
        <span className="text-[#39FF14] font-bold">Smart Optimizer</span> or{" "}
        <span className="text-[#39FF14] font-bold">Manual Optimizer</span> to start building lineups.
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
      {/* DK Upload */}
      <section className="lg:col-span-12 bg-[#121212] border border-zinc-800 p-6">
        <h2 className="heading text-2xl font-bold mb-1">DraftKings Player Pool</h2>
        <p className="text-sm text-zinc-400 mb-6">
          Sync your current DraftKings slate — start with a CSV export or use the Sync Slate button to pull the slate you're already viewing on DK.
        </p>

        {/* Auto-load dropdown */}
        <div data-testid="dk-auto-load" className="border border-[#39FF14]/40 bg-[#39FF14]/5 p-4 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <Lightning size={14} weight="fill" className="text-[#39FF14]" />
            <div className="overline text-[#39FF14]">Sync Slate · Recommended</div>
          </div>

          {/* Quick-pick chip row — surfaces the 5 most imminent labeled slates
              as one-click chips so users bypass the dropdown for the 90%
              case. Classic slates in green, showdowns in orange. Falls back
              gracefully if the picker hasn't loaded label fields yet
              (pre-refresh legacy docs). */}
          {slates.filter((s) => s.label).length > 0 && (
            <div data-testid="slate-quick-chips" className="mb-3">
              <div className="flex items-center justify-between mb-1.5 gap-2 flex-wrap">
                <div className="mono text-[10px] uppercase tracking-widest text-zinc-500">
                  Quick pick · imminent kickoff
                </div>
                {/* Slate-type filter row — one-click narrow to the format
                    the user cares about. Sunday-morning players usually
                    stick to one contest type; this cuts through the
                    15-slate noise instantly. */}
                <div className="flex items-center gap-1" data-testid="slate-type-filter">
                  {(() => {
                    const counts = slates.filter((s) => s.label).reduce((acc, s) => {
                      const t = s.slate_type || "other";
                      const bucket =
                        t === "main" ? "main" :
                        (t === "late" || t === "afternoon") ? "late" :
                        (t === "showdown" || t === "tnf" || t === "snf" || t === "mnf") ? "showdown" :
                        t === "preseason" ? "preseason" : "other";
                      acc[bucket] = (acc[bucket] || 0) + 1;
                      return acc;
                    }, {});
                    const filters = [
                      { key: "all", label: "All", count: slates.filter((s) => s.label).length },
                      { key: "main", label: "Main", count: counts.main || 0 },
                      { key: "late", label: "Late", count: counts.late || 0 },
                      { key: "showdown", label: "Showdown", count: counts.showdown || 0 },
                      { key: "preseason", label: "Preseason", count: counts.preseason || 0 },
                    ].filter((f) => f.count > 0 || f.key === "all");
                    return filters.map((f) => (
                      <button
                        key={f.key}
                        type="button"
                        data-testid={`slate-filter-${f.key}`}
                        onClick={() => setSlateFilter(f.key)}
                        disabled={autoLoading}
                        className={`px-2 py-0.5 text-[10px] mono uppercase tracking-widest transition-colors ${
                          slateFilter === f.key
                            ? "bg-white text-black font-bold"
                            : "border border-zinc-800 text-zinc-500 hover:border-zinc-600 hover:text-zinc-300"
                        }`}
                      >
                        {f.label} {f.count > 0 && <span className="opacity-60">· {f.count}</span>}
                      </button>
                    ));
                  })()}
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {slates
                  .filter((s) => s.label)
                  .filter((s) => {
                    if (slateFilter === "all") return true;
                    const t = s.slate_type || "other";
                    if (slateFilter === "main") return t === "main";
                    if (slateFilter === "late") return t === "late" || t === "afternoon";
                    if (slateFilter === "showdown") return t === "showdown" || t === "tnf" || t === "snf" || t === "mnf";
                    if (slateFilter === "preseason") return t === "preseason";
                    return true;
                  })
                  .slice(0, 5)
                  .map((s) => {
                    const active = String(selectedSlate) === String(s.dg);
                    const isSD = s.is_showdown;
                    const accent = isSD ? "#FF8A00" : "#39FF14";
                    return (
                      <button
                        key={s.dg}
                        type="button"
                        data-testid={`slate-chip-${s.dg}`}
                        onClick={() => applySlateById(s.dg)}
                        onMouseEnter={() => prefetchSlate(s.dg)}
                        onMouseLeave={cancelPrefetch}
                        onFocus={() => prefetchSlate(s.dg)}
                        onBlur={cancelPrefetch}
                        disabled={autoLoading}
                        title={`Load ${s.label} · ${s.player_count} players`}
                        className={`px-2.5 py-1.5 border transition-colors text-left disabled:opacity-40 disabled:cursor-not-allowed ${
                          active
                            ? "bg-[color:var(--accent)] text-black"
                            : "bg-black hover:bg-zinc-950"
                        }`}
                        style={{
                          "--accent": accent,
                          borderColor: active ? accent : `${accent}66`,
                        }}
                      >
                        <div
                          className="mono text-[10px] font-bold tracking-wider"
                          style={{ color: active ? "#000" : accent }}
                        >
                          {s.label}
                        </div>
                        <div className={`mono text-[9px] mt-0.5 ${active ? "text-black/70" : "text-zinc-500"}`}>
                          {s.player_count} players
                        </div>
                      </button>
                    );
                  })}
              </div>
            </div>
          )}

          <div className="flex gap-2 mb-2">
            <select
              data-testid="slate-picker"
              value={selectedSlate}
              onChange={(e) => setSelectedSlate(e.target.value)}
              className="flex-1 bg-black border border-zinc-800 text-sm px-3 py-2 mono"
            >
              <option value="">Select an active slate…</option>
              {slates.map((s) => (
                <option key={s.dg} value={s.dg}>
                  {s.label
                    ? `${s.label} · ${s.player_count} players`
                    : `${s.meta?.game_type || "NFL"} · ${s.player_count} players`}
                </option>
              ))}
            </select>
            <button
              data-testid="slate-apply"
              disabled={!selectedSlate || autoLoading}
              onClick={applySlate}
              className="px-4 py-2 bg-[#39FF14] text-black font-black text-xs tracking-widest uppercase disabled:opacity-40"
            >
              {autoLoading ? "Loading…" : "Apply"}
            </button>
          </div>
          <button
            data-testid="slate-refresh"
            disabled={autoLoading}
            onClick={refreshSlates}
            className="text-[10px] mono text-zinc-500 hover:text-[#39FF14] tracking-widest uppercase"
          >
            ↻ Refresh NFL Slates ({slates.length} cached)
          </button>
        </div>

        {(dkMeta || summary?.dk_pool_uploaded) && (
          <div
            data-testid={TID.dkUploadStatus}
            className="mt-4 grid grid-cols-3 gap-2 border-t border-zinc-800 pt-4"
          >
            <Stat label="Players" value={dkMeta?.rows ?? dkMeta?.n_players ?? summary?.dk_pool_size} />
            <Stat label="Teams" value={dkMeta?.teams?.length ?? dkMeta?.n_teams ?? (summary?.slate?.n_games ? 2 * summary.slate.n_games : "—")} />
            <Stat label="Games" value={dkMeta?.games?.length ?? dkMeta?.n_games ?? summary?.slate?.n_games ?? "—"} />
            {dkMeta && (
              <div className="col-span-3 flex flex-wrap gap-2 mt-2">
                {Object.entries(dkMeta.by_position || {}).map(([p, c]) => (
                  <span key={p} className="mono text-[11px] px-2 py-1 border border-zinc-800 text-zinc-300">
                    {p} <span className="text-[#39FF14]">{c}</span>
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </section>
      {/* Competitor projections / ownership live on the slate so both the
          Smart and Manual optimizers read the same shadow field. */}
      <section className="lg:col-span-12 space-y-4" data-testid="slate-field-model">
        <div className="text-[12px] mono uppercase tracking-widest text-zinc-400 border-l-2 border-[#FFCC00] pl-3 py-1">
          Shadow field · ingest competitor projections for this slate — used by{" "}
          <span className="text-[#FFCC00] font-bold">both optimizers</span>
        </div>
        <FieldModelPanel />
        <LeverageBoard />
        <CompetitorPoolGrid />
      </section>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="overline">{label}</div>
      <div className="heading text-2xl font-bold text-[#39FF14] mono">{value ?? "—"}</div>
    </div>
  );
}

function Card({ step, title, body }) {
  return (
    <div className="border border-zinc-800 p-4">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-6 h-6 flex items-center justify-center bg-[#39FF14] text-black text-xs font-black">
          {step}
        </div>
        <div className="heading font-semibold">{title}</div>
      </div>
      <div className="text-sm text-zinc-400 leading-relaxed">{body}</div>
    </div>
  );
}
